import type { Message, MessageCall } from '@nexo/types';
import { AvatarStack, Button, cn } from '@nexo/ui';
import { Phone, PhoneOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTeam } from '../../layouts/TeamContext';
import { useChannelCall, useStartCall } from './CallControls';
import { t as translate } from '../../lib/i18n';
import { callStore, useCall } from './callStore';

function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return translate('{{h}} h {{m}} min', { h, m });
  if (m > 0 && s > 0 && m < 10) return translate('{{m}} min {{s}} s', { m, s });
  if (m > 0) return translate('{{n}} min', { n: m });
  return translate('{{n}} s', { n: s });
}

/** Ticks every second while `active`, for the live call timer. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

/** Timeline card for a call in a text conversation: live with a join button, or its summary. */
export function CallRecord({ message, call }: { message: Message; call: MessageCall }) {
  const { memberById } = useTeam();
  const live = useChannelCall(message.channelId);
  const current = useCall();
  const { t } = useTranslation();
  // Open records are live while the call is listed. A record that just appeared counts as live
  // too (the "who is in the call" update may arrive a moment later); a lost webhook never leaves
  // an old card stuck "in progress".
  const now = useNow(call.endedAt === null);
  const justStarted = now - new Date(call.startedAt).getTime() < 60_000;
  const ongoing = call.endedAt === null && (live !== undefined || justStarted);
  const inCall = current?.join.channelId === message.channelId;

  const people = (live ? live.participants : call.participantIds.map((id) => memberById(id)))
    .filter((p) => p !== undefined)
    .map((p) => ({ id: p.id, displayName: p.displayName, avatarUrl: p.avatarUrl }));
  const started = new Date(call.startedAt).getTime();
  const duration = call.endedAt ? new Date(call.endedAt).getTime() - started : now - started;

  const startCall = useStartCall();
  const join = () => startCall(message.channelId, false);

  return (
    <div
      className={cn(
        'mt-1 flex max-w-md items-center gap-3 rounded-xl border p-3',
        ongoing ? 'border-green-600/40 bg-green-600/10' : 'border-border bg-surface-2',
      )}
    >
      <span
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-full',
          ongoing ? 'bg-green-600 text-white' : 'bg-surface text-muted',
        )}
      >
        {ongoing ? <Phone className="size-5" /> : <PhoneOff className="size-5" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">
          {ongoing ? t('Llamada en curso') : t('Llamada finalizada')}
        </p>
        <p className="text-xs text-muted tabular-nums">
          {call.endedAt || ongoing ? formatDuration(duration) : t('Duración desconocida')}
          {people.length > 0 &&
            ` · ${people.length === 1 ? t('1 persona') : t('{{count}} personas', { count: people.length })}`}
        </p>
      </div>
      {people.length > 0 && <AvatarStack people={people} max={4} size="xs" />}
      {ongoing &&
        (inCall ? (
          <Button size="sm" variant="secondary" onClick={() => callStore.setExpanded(true)}>
            {t('Abrir')}
          </Button>
        ) : (
          <Button size="sm" icon={<Phone className="size-3.5" />} onClick={() => void join()}>
            {t('Unirse')}
          </Button>
        ))}
    </div>
  );
}
